/**
 * FdrVendorPortal — the vendor-facing FDR Compliance Portal. Public route
 * (/fdr-portal?token=...), no Core session, no app shell.
 *
 * Flow: invite link → email OTP → 15-minute pass → purpose view:
 *   attestation: canonical text, the sponsor's own evidence beside each element,
 *                nine confirmations, click-wrap e-sign (ESIGN bundle server-side)
 *   thread:      record-anchored secure conversation + reply
 *
 * Respondent UX per the researched spec: no passwords ever, autoless entry,
 * everything in one session.
 */
import React, { useState, useEffect, useCallback } from 'react';
import pcgLighthouse from '../assets/pcg-lighthouse.png';

const API = process.env.REACT_APP_API_URL || '';

const card = 'bg-white rounded-2xl border border-slate-200/60 shadow-sm';
const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
const btn = 'px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all disabled:opacity-40 disabled:shadow-none';

export default function FdrVendorPortal() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [stage, setStage] = useState('verify');   // verify | code | main | error
  const [ctx, setCtx] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [pass, setPass] = useState(null);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [logoOk, setLogoOk] = useState(true);

  const post = useCallback(async (path, body) => {
    const res = await fetch(`${API}/api/fdr-portal${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...(pass ? { 'x-portal-pass': pass } : {}) },
      body: JSON.stringify({ token, ...body }),
    });
    return res.json();
  }, [token, pass]);

  useEffect(() => {
    (async () => {
      if (!token) { setStage('error'); setError('This link is missing its token.'); return; }
      const d = await post('/verify').catch(() => null);
      if (d?.success) { setCtx(d.context); setStage('verify'); }
      else { setStage('error'); setError(d?.message || 'This link is invalid or has expired.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestCode = async () => {
    setBusy(true); setError('');
    const d = await post('/mfa/challenge').catch(() => null);
    if (d?.success) { setChallenge(d); setStage('code'); }
    else setError(d?.message || 'Could not send the code.');
    setBusy(false);
  };

  const verifyCode = async () => {
    setBusy(true); setError('');
    const d = await post('/mfa/verify', { challenge_id: challenge.challenge_id, code: code.trim() }).catch(() => null);
    if (d?.success) {
      setPass(d.pass_token);
      const me = await fetch(`${API}/api/fdr-portal/me?token=${encodeURIComponent(token)}`, {
        headers: { 'x-portal-pass': d.pass_token },
      }).then(r => r.json()).catch(() => null);
      if (me?.success) { setPayload(me); setStage('main'); }
      else setError(me?.message || 'Failed to load.');
    } else setError(d?.message || 'Code is incorrect.');
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Masthead: the CLIENT's brand — their Deck Studio logo and name —
            with the vendor named beneath. Falls back to the shield icon when
            no logo is on file. */}
        <div className="flex items-center gap-3 mb-6">
          {/* The client's mark and name — nothing else. No placeholder icon
              ever: without a logo on file, the name alone carries the header. */}
          {logoOk && (
            <img src={`${API}/api/fdr-portal/logo?token=${encodeURIComponent(token)}`} alt={ctx?.org_name || 'logo'}
              onError={() => setLogoOk(false)}
              className="h-11 max-w-[190px] object-contain bg-white rounded-xl border border-slate-200 px-2 py-1.5" />
          )}
          <div>
            <div className="text-lg font-bold text-slate-800">{ctx?.org_name || 'FDR Compliance Portal'}</div>
            <div className="text-xs text-slate-500">FDR Compliance Portal{ctx?.entity_name ? ` · for ${ctx.entity_name}` : ''}</div>
          </div>
        </div>

        {stage === 'error' && (
          <div className={`${card} p-8 text-center`}>
            <p className="text-slate-700 font-medium">{error}</p>
            <p className="text-sm text-slate-400 mt-2">Contact your sponsor's compliance team for a new link.</p>
          </div>
        )}

        {stage === 'verify' && ctx && (
          <div className={`${card} p-8`}>
            <h2 className="text-base font-bold text-slate-800">
              {ctx.contact_name ? `Hello ${ctx.contact_name},` : 'Hello,'}
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              {ctx.org_name} has requested {ctx.purpose === 'attestation'
                ? 'your signature on an annual FDR compliance attestation'
                : ctx.purpose === 'assessment'
                  ? 'your organization’s response to an FDR compliance audit'
                  : 'your response in a secure compliance conversation'} for <strong>{ctx.entity_name}</strong>.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              To continue, we'll email a 6-digit code to your address on file. No account or password is needed.
            </p>
            {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2 mt-3">{error}</p>}
            <button onClick={requestCode} disabled={busy} className={`${btn} mt-5`}>
              {busy ? 'Sending…' : 'Email me the code'}
            </button>
          </div>
        )}

        {stage === 'code' && (
          <div className={`${card} p-8`}>
            <h2 className="text-base font-bold text-slate-800">Enter your code</h2>
            <p className="text-sm text-slate-500 mt-1">Sent to {challenge?.recipient_masked}. It expires in 10 minutes.</p>
            <input autoFocus value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && verifyCode()}
              className="mt-4 w-48 text-center tracking-[0.5em] font-mono text-2xl border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              placeholder="······" />
            {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2 mt-3">{error}</p>}
            <div className="flex items-center gap-3 mt-5">
              <button onClick={verifyCode} disabled={busy || code.length !== 6} className={btn}>
                {busy ? 'Verifying…' : 'Continue'}
              </button>
              <button onClick={requestCode} disabled={busy} className="text-sm text-slate-500 hover:text-slate-700">Resend code</button>
            </div>
          </div>
        )}

        {stage === 'main' && payload?.attestation && (
          <AttestationSign payload={payload} post={post} token={token} pass={pass} />
        )}
        {stage === 'main' && payload?.assessment && (
          <AssessmentRespond payload={payload} post={post} token={token} pass={pass} />
        )}
        {stage === 'main' && payload?.thread && (
          <ThreadView payload={payload} post={post} token={token} pass={pass} />
        )}

        {/* The trust footer: the client owns the masthead above; the platform
            signs the bottom — the PCG lighthouse, strategically alone. */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <img src={pcgLighthouse} alt="Precision Compliance Group" className="h-9 object-contain opacity-90" />
          <p className="text-[11px] text-slate-400 text-center">
            Secured by Precision Compliance Group · verification and signatures are recorded with timestamp, IP, and authentication method.
          </p>
        </div>
      </div>
    </div>
  );
}

/** One question, rendered by its response contract. */
function QuestionInput({ q, value, onChange }) {
  const base = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
  if (q.response_type === 'yes_no') {
    return (
      <div className="flex gap-2">
        {[['yes', 'Confirm'], ['no', 'Cannot confirm']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              value === v
                ? v === 'yes' ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-amber-500 border-amber-500 text-white'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
            {l}
          </button>
        ))}
      </div>
    );
  }
  if (q.response_type === 'text') return <textarea rows={2} value={value || ''} onChange={e => onChange(e.target.value)} className={base} />;
  if (q.response_type === 'number') return <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value)} className={`${base} max-w-[180px]`} />;
  if (q.response_type === 'date') return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className={`${base} max-w-[200px]`} />;
  if (q.response_type === 'select') {
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map(o => (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${value === o ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (q.response_type === 'multi_select') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map(o => (
          <button key={o} type="button"
            onClick={() => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o])}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${arr.includes(o) ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
            {arr.includes(o) ? '✓ ' : ''}{o}
          </button>
        ))}
      </div>
    );
  }
  return null;
}

/** Attach supporting evidence (SOC 2, policies, training logs) to this grant. */
function EvidenceUpload({ token, pass }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/fdr-portal/documents?token=${encodeURIComponent(token)}`, {
      headers: { 'x-portal-pass': pass },
    }).then(r => r.json()).catch(() => null);
    if (r?.success) setDocs(r.documents || []);
  }, [token, pass]);
  useEffect(() => { load(); }, [load]);

  const send = async (files) => {
    setError('');
    for (const f of Array.from(files || [])) {
      if (f.size > 25 * 1048576) { setError(`Files can be up to 25 MB; "${f.name}" is ${(f.size / 1048576).toFixed(1)} MB.`); continue; }
      setBusy(true);
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/api/fdr-portal/documents?token=${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'x-portal-pass': pass }, body: fd,
      }).then(r => r.json()).catch(() => null);
      if (!r?.success) setError(r?.message || `Could not upload "${f.name}".`);
    }
    setBusy(false);
    load();
  };

  return (
    <div className={`${card} p-6`}>
      <h3 className="text-sm font-bold text-slate-800">Supporting documents</h3>
      <p className="text-xs text-slate-500 mt-1">Attach the evidence behind your answers — SOC 2 report, policies, training logs. PDF, Word, Excel, CSV, text, images, or a ZIP of several documents, up to 25 MB each. Select as many files as you need at once. Files are encrypted at rest; text is screened for member PHI, which is not accepted here.</p>
      {docs.length > 0 && (
        <div className="mt-3 space-y-1">
          {docs.map(d => (
            <div key={d.id} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">
              <span>📎</span><span className="truncate flex-1">{d.file_name}</span>
              {d.extraction_status === 'blocked_phi'
                ? <span className="text-red-600 font-semibold">Removed — contained PHI</span>
                : <span className="text-emerald-600 font-semibold">Received</span>}
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-2">{error}</p>}
      <label className="mt-3 block w-full text-center border border-dashed border-slate-300 rounded-xl px-4 py-4 text-sm text-slate-400 hover:border-slate-400 hover:text-slate-600 cursor-pointer">
        {busy ? 'Uploading…' : 'Click to add files, or drop them here'}
        <input type="file" multiple className="hidden" disabled={busy}
          onChange={e => { send(e.target.files); e.target.value = ''; }} />
      </label>
    </div>
  );
}

function AttestationSign({ payload, post, token, pass }) {
  const a = payload.attestation;
  // Typed questions (snapshot) with a legacy fallback shape.
  const questions = a.questions || a.elements.map(e => ({ ...e, response_type: 'yes_no', required: true, requires_explanation_on: 'no' }));
  const [answers, setAnswers] = useState(() => Object.fromEntries(
    questions.filter(q => q.response_type === 'yes_no').map(q => [q.key, undefined])));
  const [scrolled, setScrolled] = useState(false);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState('');
  const [consent, setConsent] = useState(false);
  const [exceptions, setExceptions] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cert, setCert] = useState(null);

  const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
  const unanswered = questions.filter(q => q.required !== false && isEmpty(answers[q.key]));
  const flagged = questions.filter(q => {
    const v = answers[q.key];
    if (q.response_type === 'yes_no' && v === 'no') return true;
    return q.requires_explanation_on && !isEmpty(v) && String(v) === String(q.requires_explanation_on);
  });
  const needsException = flagged.length > 0;
  const canSign = scrolled && name.trim() && title.trim() && role && consent &&
    unanswered.length === 0 && (!needsException || exceptions.trim().length >= 10);

  const sign = async () => {
    setBusy(true); setError('');
    const d = await post('/attestation/sign', {
      signer_name: name, signer_title: title, signer_role: role,
      consent: true, elements: answers, exceptions_note: exceptions || undefined,
    }).catch(() => null);
    if (d?.success) setCert(d.certificate);
    else setError(d?.message || 'Failed to record signature.');
    setBusy(false);
  };

  if (a.already_signed && !cert) {
    return (
      <div className={`${card} p-8 text-center`}>
        <p className="text-slate-700 font-medium">This attestation was signed by {a.already_signed.by} on {String(a.already_signed.at).split('T')[0]}.</p>
        <p className="text-sm text-slate-400 mt-2">No further action is needed.</p>
      </div>
    );
  }

  if (cert) {
    return (
      <div className={`${card} p-8 text-center`}>
        <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
        </div>
        <h2 className="text-lg font-bold text-slate-800 mt-3">Attestation signed</h2>
        <p className="text-sm text-slate-600 mt-1">{cert.confirmed} of {questions.length} items confirmed{cert.unconfirmed.length ? ` · ${cert.unconfirmed.length} exception(s) recorded` : ''}.</p>
        <div className="text-left text-xs text-slate-500 bg-slate-50 rounded-xl p-4 mt-4 font-mono break-all">
          Signature ID: {cert.signature_id}<br/>Signed: {cert.signed_at}<br/>Text {cert.text_version} · SHA-256 {cert.text_sha256}
        </div>
        <p className="text-xs text-slate-400 mt-3">A copy of this certificate is retained by the sponsor for ten years. You may close this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={`${card} p-6`}>
        <h2 className="text-base font-bold text-slate-800">CY{a.year} Annual FDR Compliance Attestation</h2>
        <p className="text-xs text-slate-500 mt-0.5">Requested by <strong>{payload.org_name}</strong> for <strong>{payload.entity_name}</strong></p>
        <p className="text-xs text-slate-400 mt-0.5">Due {String(a.due_date || '').split('T')[0]} · text {a.text_version}</p>
        <div onScroll={e => { const el = e.target; if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setScrolled(true); }}
          className="mt-3 h-44 overflow-y-auto bg-slate-50 rounded-xl p-4 text-xs text-slate-600 whitespace-pre-wrap border border-slate-100">
          {a.text}
        </div>
        {!scrolled && <p className="text-[11px] text-amber-600 mt-1.5">Scroll the full text to enable signing.</p>}
      </div>

      {/* Data-bearing evidence: the sponsor's own record beside the promise */}
      <div className={`${card} p-6`}>
        <h3 className="text-sm font-bold text-slate-800">The sponsor's record for CY{a.year}</h3>
        <p className="text-xs text-slate-500 mt-1">
          Exclusion screening runs on file: <strong>{a.sponsor_evidence.screening_runs_this_year}</strong>
          {a.sponsor_evidence.last_screening ? ` (last ${String(a.sponsor_evidence.last_screening).split('T')[0]})` : ''} ·
          Oversight activities logged: <strong>{(a.sponsor_evidence.oversight_activities || []).reduce((n, x) => n + x.n, 0)}</strong>
        </p>
        <p className="text-[11px] text-slate-400 mt-1">Confirm only what your organization actually operates. Anything you cannot confirm becomes a documented exception, not a hidden gap.</p>
      </div>

      <div className={`${card} p-6`}>
        <h3 className="text-sm font-bold text-slate-800 mb-2">Your answers</h3>
        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={q.key}>
              <div className="text-sm text-slate-700 mb-1.5">
                <span className="text-slate-400 font-mono text-xs mr-1.5">{i + 1}.</span>{q.label}
                {q.required === false && <span className="text-[11px] text-slate-400 ml-1.5">(optional)</span>}
              </div>
              {q.help_text && <div className="text-[11px] text-slate-400 mb-1.5 ml-5">{q.help_text}</div>}
              <div className="ml-5">
                <QuestionInput q={q} value={answers[q.key]} onChange={(v) => setAnswers(s => ({ ...s, [q.key]: v }))} />
              </div>
            </div>
          ))}
        </div>
        {needsException && (
          <div className="mt-4">
            <label className="block text-xs font-semibold text-amber-700 mb-1">
              {flagged.length} answer(s) need a written explanation — it becomes the documented exception, not a hidden gap:
            </label>
            <textarea rows={3} value={exceptions} onChange={e => setExceptions(e.target.value)} className={inputCls} />
          </div>
        )}
        {unanswered.length > 0 && (
          <p className="text-[11px] text-slate-400 mt-3">{unanswered.length} required answer(s) remaining before you can sign.</p>
        )}
      </div>

      <EvidenceUpload token={token} pass={pass} />

      <div className={`${card} p-6`}>
        <h3 className="text-sm font-bold text-slate-800 mb-3">Signature</h3>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Type your full legal name *" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
          <input placeholder="Your title *" value={title} onChange={e => setTitle(e.target.value)} className={inputCls} />
        </div>
        <select value={role} onChange={e => setRole(e.target.value)} className={`${inputCls} mt-3`}>
          <option value="">Your authorized-representative role *</option>
          <option value="compliance_officer">Compliance Officer</option>
          <option value="ceo_executive">CEO / Executive</option>
          <option value="medical_director">Medical Director</option>
          <option value="administrator">Administrator</option>
        </select>
        <label className="flex items-start gap-2.5 text-xs text-slate-600 mt-3 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
          <span>I consent to conduct this transaction electronically, I am authorized to attest on behalf of this entity and its downstream entities, and I understand my typed name constitutes my legal signature (ESIGN/UETA).</span>
        </label>
        {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2 mt-3">{error}</p>}
        <button onClick={sign} disabled={!canSign || busy} className={`${btn} mt-4 w-full`}>
          {busy ? 'Recording signature…' : `Sign attestation as ${name.trim() || '…'}`}
        </button>
      </div>
    </div>
  );
}

function ThreadView({ payload, post, token, pass }) {
  const [thread, setThread] = useState(payload.thread);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true); setError('');
    const d = await post('/thread/reply', { body: reply.trim() }).catch(() => null);
    if (d?.success) {
      setThread(t => ({ ...t, messages: [...t.messages, { author_type: 'vendor', author_name: 'You', body: reply.trim(), created_at: d.created_at }] }));
      setReply('');
    } else setError(d?.message || 'Failed to send.');
    setBusy(false);
  };

  return (
    <div className="space-y-4">
    <div className={`${card} p-6`}>
      <h2 className="text-base font-bold text-slate-800">{thread.subject}</h2>
      <p className="text-xs text-slate-400 mt-0.5 capitalize">{thread.record_kind} · secure conversation with {payload.org_name}</p>
      <div className="mt-4 space-y-3 max-h-[380px] overflow-y-auto">
        {thread.messages.map((m, i) => (
          <div key={i} className={`flex ${m.author_type === 'vendor' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.author_type === 'vendor' ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-br-md' : 'bg-slate-50 text-slate-700 rounded-bl-md'}`}>
              <div className={`text-[10px] font-bold mb-0.5 ${m.author_type === 'vendor' ? 'text-orange-100' : 'text-slate-400'}`}>
                {m.author_name} · {String(m.created_at).split('T')[0]}
              </div>
              <div className="whitespace-pre-wrap">{m.body}</div>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2 mt-3">{error}</p>}
      <div className="flex gap-2 mt-4">
        <textarea rows={2} value={reply} onChange={e => setReply(e.target.value)} placeholder="Write your response…" className={inputCls} />
        <button onClick={send} disabled={busy || !reply.trim()} className={btn}>{busy ? '…' : 'Send'}</button>
      </div>
      <p className="text-[11px] text-slate-300 mt-2">Every message is recorded in the sponsor's compliance file.</p>
    </div>
    <EvidenceUpload token={token} pass={pass} />
    </div>
  );
}

/**
 * AssessmentRespond — the vendor's side of the annual assessment, four plain
 * steps anyone can follow: read and acknowledge the notice, answer each
 * audit tool element, attach the documents, sign each tool's attestation and
 * submit. The sponsor's verdicts never appear here.
 */
/**
 * The compliance officer's team panel (operator order 2026-08-25): invite
 * colleagues who are already on the vendor's contact list with the sponsor,
 * optionally limited to one audit tool. Contributors answer, upload, and
 * ask; the compliance officer alone signs and submits.
 */
function TeamPanel({ post, token, pass }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [contactId, setContactId] = useState('');
  const [toolScope, setToolScope] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const load = async () => {
    const r = await fetch(`${API}/api/fdr-portal/assessment/participants?token=${encodeURIComponent(token)}`, {
      headers: { 'x-portal-pass': pass },
    }).then((x) => x.json()).catch(() => null);
    if (r?.success) setData(r);
  };

  if (!open) {
    return (
      <button type="button" className="text-xs font-semibold text-orange-700 hover:text-orange-800 underline"
        onClick={() => { setOpen(true); setNote(''); load(); }}>
        Working with teammates? Invite a colleague
      </button>
    );
  }
  const contacts = (data?.contacts || []).filter((c) => !(data?.participants || []).some((p) => p.email && c.email && p.email.toLowerCase() === c.email.toLowerCase()));
  return (
    <div className="mt-2 border border-slate-200 rounded-xl p-4 bg-slate-50/60">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Your team on this audit</h4>
        <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setOpen(false)}>Close</button>
      </div>
      {(data?.participants || []).length > 0 && (
        <ul className="mt-2 space-y-1">
          {data.participants.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-xs text-slate-600">
              <span><b>{p.name || p.email}</b> · {p.role === 'primary' ? 'compliance officer' : (p.tool_scope || 'all audit tools')}{p.role !== 'primary' && !p.joined ? ' · invited' : ''}</span>
              {p.role !== 'primary' && (
                <button type="button" className="text-rose-600 hover:text-rose-700"
                  onClick={async () => {
                    const r = await fetch(`${API}/api/fdr-portal/assessment/participants/${p.id}?token=${encodeURIComponent(token)}`, {
                      method: 'DELETE', headers: { 'x-portal-pass': pass },
                    }).then((x) => x.json()).catch(() => null);
                    if (r?.success) load(); else setNote(r?.message || 'Could not remove access.');
                  }}>Remove</button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex items-end gap-2 flex-wrap">
        <div className="min-w-[13rem]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Colleague</div>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white">
            <option value="">Choose from your contact list…</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(' ')}{c.title ? ` — ${c.title}` : ''}</option>)}
          </select>
        </div>
        <div className="min-w-[11rem]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Their part</div>
          <select value={toolScope} onChange={(e) => setToolScope(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white">
            <option value="">Every audit tool</option>
            {(data?.tools || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button type="button" disabled={busy || !contactId}
          className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50"
          onClick={async () => {
            setBusy(true); setNote('');
            const r = await post('/assessment/participants', { contact_id: contactId, tool_scope: toolScope || null });
            if (r?.success) { setContactId(''); setToolScope(''); setNote('Invite sent — they verify with a code to their own email.'); load(); }
            else setNote(r?.message || 'Could not send the invite.');
            setBusy(false);
          }}>{busy ? 'Inviting…' : 'Send invite'}</button>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">
        Only people on your organization's contact list with the sponsor can be invited. Colleagues answer and attach documents; you sign and submit.
        {note && <span className="block mt-1 text-slate-600 font-medium">{note}</span>}
      </p>
    </div>
  );
}

function AssessmentRespond({ payload, post, token, pass }) {
  const [a, setA] = useState(payload.assessment);
  // Contributors (invited colleagues) work the tools and documents; the
  // compliance officer alone acknowledges, signs, and submits.
  const isContributor = payload.assessment.participant_role === 'contributor';
  const [step, setStep] = useState(() => (isContributor ? 2 : payload.assessment.submitted_at ? 5 : payload.assessment.ack_at ? 2 : 1));
  const [tool, setTool] = useState(payload.assessment.items[0]?.tool_name || null);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [ackName, setAckName] = useState('');
  const [signForms, setSignForms] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const me = await fetch(`${API}/api/fdr-portal/me?token=${encodeURIComponent(token)}`, {
      headers: { 'x-portal-pass': pass },
    }).then(r => r.json()).catch(() => null);
    if (me?.success) setA(me.assessment);
  }, [token, pass]);

  const saveItem = async (itemId, fields) => {
    setA(prev => ({ ...prev, items: prev.items.map(i => (i.id === itemId ? { ...i, ...fields } : i)) }));
    const r = await fetch(`${API}/api/fdr-portal/assessment/items/${itemId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'x-portal-pass': pass },
      body: JSON.stringify({ token, ...fields }),
    }).then(r => r.json()).catch(() => null);
    if (!r?.success) setError(r?.message || 'Could not save — check your connection.');
  };

  // Answered means what the element asked for: words, a document (an upload
  // fills the named-documents field, and carry-forward counts), or either.
  const isAnswered = (i) => (i.vendor_response && i.vendor_response.trim()) || i.evidence_ref || i.doc_status === 'previous_effective';
  const answered = a.items.filter(isAnswered).length;
  const tools = [...new Set(a.items.map(i => i.tool_name))];
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }) : '');
  // The portal never locks while the audit is open: the vendor saves,
  // uploads, comments, and resubmits as they complete work. Only the sponsor
  // closing the engagement ends the window (the link stops resolving).
  const readOnly = false;

  const STEPS = [
    { n: 1, label: 'Review the notice' },
    { n: 2, label: 'Complete the audit tools' },
    { n: 3, label: 'Attach documents' },
    { n: 4, label: 'Sign & submit' },
  ];
  const stepDone = { 1: !!a.ack_at, 2: answered === a.items.length && a.items.length > 0, 3: (a.documents || []).length > 0, 4: !!a.submitted_at };

  if (step === 5) {
    return (
      <div className="space-y-4">
        <div className={`${card} p-8 text-center`}>
          <div className="text-3xl">✓</div>
          <h2 className="text-base font-bold text-slate-800 mt-2">Submitted — thank you</h2>
          <p className="text-sm text-slate-600 mt-2">
            Your response to the {a.cycle_label} assessment was submitted{a.submitted_at ? ` on ${fmt(a.submitted_at)}` : ''}.
            The {payload.org_name} compliance team will review it and follow up with the audit results.
          </p>
          <p className="text-xs text-slate-400 mt-3">You answered {answered} of {a.items.length} elements and attached {(a.documents || []).length} document(s). A record of your signatures is retained.</p>
          <button className="mt-4 px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => setStep(2)}>
            Continue working on your response
          </button>
          <p className="mt-2 text-[11px] text-slate-400">You can keep answering, uploading, and commenting until the audit closes — submit again whenever you add more.</p>
        </div>
        {/* The audit continues after submission: universes and sampled case
            files are requested during review and are provided here. */}
        {((a.universe_requests || []).length > 0 || (a.sample_requests || []).length > 0) && (
          <UniverseSlots a={a} token={token} pass={pass} refresh={refresh} highlightPending />
        )}
        {a.report && (
          <ReportCard a={a} token={token} pass={pass} post={post} refresh={refresh} />
        )}
      </div>
    );
  }

  return (
    <div>
      {/* The four steps, always visible — anyone can see where they are. */}
      <div className={`${card} px-4 py-3 mb-4 flex items-center gap-1.5 flex-wrap`}>
        {STEPS.map(s => (
          <button key={s.n} onClick={() => setStep(s.n)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${step === s.n ? 'bg-orange-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>
            <span className={`w-4 h-4 rounded-full grid place-items-center text-[10px] ${stepDone[s.n] ? 'bg-emerald-500 text-white' : step === s.n ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-500'}`}>
              {stepDone[s.n] ? '✓' : s.n}
            </span>
            {s.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-2">
          <button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 12000); }}
            title="Everything saves as you type — this confirms it and tells you how to come back"
            className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-500 hover:text-slate-700 text-[11px] font-semibold">
            Save &amp; finish later
          </button>
          <span className="text-[11px] text-slate-400">Due {fmt(a.response_deadline) || 'see notice'}</span>
        </span>
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2 mb-3">{error}</p>}

      {isContributor && (
        <div className="mb-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 text-xs text-orange-800">
          You were invited to contribute{a.tool_scope ? <> to <b>{a.tool_scope}</b></> : ''}. Answer the elements, attach documents, and ask the audit team questions — your compliance officer reviews everything, signs the attestations, and submits.
        </div>
      )}

      {step === 1 && (
        <div className={`${card} p-6`}>
          <h3 className="text-sm font-bold text-slate-800">The engagement letter</h3>
          <p className="text-xs text-slate-500 mt-1">This is the formal audit notice. Read it, then acknowledge receipt below — that is all this step asks.</p>
          <div className="mt-3 max-h-96 overflow-y-auto bg-slate-50 border border-slate-200 rounded-xl p-4 text-[12.5px] text-slate-700 whitespace-pre-wrap leading-relaxed">
            {a.letter_text || 'The engagement letter will appear here.'}
          </div>
          {isContributor ? (
            <p className="mt-3 text-xs text-slate-500">{a.ack_at ? `Receipt acknowledged by ${a.ack_name} on ${fmt(a.ack_at)}.` : 'Your compliance officer acknowledges the notice.'}</p>
          ) : a.ack_at ? (
            <p className="mt-3 text-xs font-semibold text-emerald-700">Receipt acknowledged by {a.ack_name} on {fmt(a.ack_at)}. Continue to the audit tools.</p>
          ) : (
            <div className="mt-4 flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[14rem]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Your full name</div>
                <input value={ackName} onChange={e => setAckName(e.target.value)} className={inputCls} placeholder="Type your name to acknowledge receipt" />
              </div>
              <button disabled={busy || !ackName.trim()} className={btn} onClick={async () => {
                setBusy(true); setError('');
                const r = await post('/assessment/ack', { name: ackName.trim() });
                if (r?.success) { await refresh(); setStep(2); } else setError(r?.message || 'Could not record it.');
                setBusy(false);
              }}>{busy ? 'Recording…' : 'Acknowledge receipt'}</button>
            </div>
          )}
          {a.ack_at && <button className={`${btn} mt-4`} onClick={() => setStep(2)}>Continue →</button>}
        </div>
      )}

      {step === 2 && (
        <div className={`${card} p-6`}>
          <h3 className="text-sm font-bold text-slate-800">Complete the audit tools</h3>
          <p className="text-xs text-slate-500 mt-1">
            For each element: describe how your organization meets it, and name the documents that prove it.
            If a policy has not changed since last year, pick &ldquo;Previous document remains effective.&rdquo; Your work saves automatically.
          </p>
          {!isContributor && <div className="mt-2"><TeamPanel post={post} token={token} pass={pass} /></div>}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            {tools.map(t => (
              <button key={t} onClick={() => setTool(t)}
                className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${tool === t ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {t.length > 34 ? `${t.slice(0, 32)}…` : t}
              </button>
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
              <input type="checkbox" checked={onlyOpen} onChange={() => setOnlyOpen(v => !v)} className="accent-orange-600" />
              Show unanswered only
            </label>
          </div>
          <div className="mt-2 text-[11px] text-slate-400">{answered} of {a.items.length} elements answered</div>
          <div className="mt-2 max-h-[32rem] overflow-y-auto space-y-2 pr-1">
            {(() => {
              let lastSection = null;
              return a.items.filter(i => i.tool_name === tool && (!onlyOpen || !isAnswered(i))).map(i => {
                const showSection = i.section !== lastSection;
                lastSection = i.section;
                return (
                  <React.Fragment key={i.id}>
                    {showSection && <div className="pt-2 text-[10px] font-bold uppercase tracking-wider text-orange-700">{i.section}</div>}
                    <div className="border border-slate-200 rounded-xl p-3">
                      <span className="inline-flex items-center gap-1.5 mb-1">
                        {i.label_path && <span className="inline-block px-1.5 py-px rounded bg-slate-100 text-[10px] font-semibold text-slate-500">{i.label_path}</span>}
                        {(i.response_mode === 'attachment' || i.response_mode === 'both') && (
                          <span className="inline-block px-1.5 py-px rounded bg-sky-50 text-[10px] font-semibold text-sky-700">Document requested</span>
                        )}
                      </span>
                      <div className="text-[12.5px] text-slate-800 leading-snug">{i.criteria_text}</div>
                      {i.guidance && (
                        <div className="mt-0.5 text-[11px] text-slate-500 leading-snug whitespace-pre-wrap">{i.guidance}</div>
                      )}
                      {i.response_mode !== 'attachment' && (
                        <textarea rows={2} defaultValue={i.vendor_response || ''} disabled={readOnly}
                          placeholder="How does your organization meet this?"
                          onBlur={e => e.target.value !== (i.vendor_response || '') && saveItem(i.id, { vendor_response: e.target.value })}
                          className="mt-2 w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-orange-400" />
                      )}
                      <div className="mt-1.5 flex gap-2 flex-wrap">
                        <input defaultValue={i.evidence_ref || ''} disabled={readOnly}
                          placeholder="Documents that support this (names)"
                          onBlur={e => e.target.value !== (i.evidence_ref || '') && saveItem(i.id, { evidence_ref: e.target.value })}
                          className="flex-1 min-w-[12rem] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-orange-400" />
                        <select value={i.doc_status || ''} disabled={readOnly}
                          onChange={e => saveItem(i.id, { doc_status: e.target.value || null })}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-[11px] bg-white text-slate-600">
                          <option value="">Document status…</option>
                          <option value="new_updated">New / updated document submitted</option>
                          <option value="previous_effective">Previous document remains effective</option>
                        </select>
                        {!readOnly && (
                          <ItemUpload token={token} pass={pass} item={i}
                            onUploaded={(fileName) => {
                              const named = i.evidence_ref ? `${i.evidence_ref}; ${fileName}` : fileName;
                              saveItem(i.id, { evidence_ref: named, doc_status: i.doc_status || 'new_updated' });
                              refresh();
                            }} />
                        )}
                        {!readOnly && <ItemQuestion token={token} pass={pass} item={i} />}
                      </div>
                    </div>
                  </React.Fragment>
                );
              });
            })()}
          </div>
          <button className={`${btn} mt-4`} onClick={() => setStep(3)}>Continue to documents →</button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          {(a.universe_requests || []).length > 0 && (
            <UniverseSlots a={a} token={token} pass={pass} refresh={refresh} />
          )}
          <EvidenceUpload token={token} pass={pass} />
          <button className={btn} onClick={() => setStep(4)}>Continue to sign &amp; submit →</button>
        </div>
      )}
      {a.report && (
        <div className="mt-4">
          <ReportCard a={a} token={token} pass={pass} post={post} refresh={refresh} />
        </div>
      )}
      {saved && (
        <div className={`${card} p-5 mt-4 text-center`}>
          <p className="text-sm font-semibold text-emerald-700">All of your work is saved.</p>
          <p className="text-xs text-slate-500 mt-1">Come back anytime using the secure link in your email — we&rsquo;ll send a fresh verification code and you&rsquo;ll pick up exactly where you left off.</p>
        </div>
      )}

      {step === 4 && isContributor && (
        <div className={`${card} p-6`}>
          <h3 className="text-sm font-bold text-slate-800">Signing and submitting</h3>
          <p className="text-sm text-slate-600 mt-2">
            Your compliance officer signs the attestation and submits the response for {payload.entity_name || 'your organization'}.
            Your answers and documents are already saved and visible to them — nothing more is needed from you on this step.
          </p>
          {(a.attestations || []).some((x) => x.signed_at) && (
            <p className="text-xs font-semibold text-emerald-700 mt-3">
              {(a.attestations || []).filter((x) => x.signed_at).length} attestation(s) signed so far.
            </p>
          )}
        </div>
      )}

      {step === 4 && !isContributor && (
        <div className={`${card} p-6`}>
          <h3 className="text-sm font-bold text-slate-800">Sign each attestation, then submit</h3>
          <p className="text-xs text-slate-500 mt-1">An authorized representative certifies each audit tool. Typing your name and clicking Sign is your electronic signature — recorded with timestamp, IP, and the exact text signed.</p>
          <div className="mt-3 space-y-3">
            {(a.attestations || []).map(att => {
              const f = signForms[att.id] || { name: '', title: '', consent: false };
              const setF = (patch) => setSignForms(prev => ({ ...prev, [att.id]: { ...f, ...patch } }));
              return (
                <div key={att.id} className="border border-slate-200 rounded-xl p-4">
                  <div className="text-[12.5px] font-bold text-slate-800">{att.tool_name}</div>
                  <div className="mt-2 max-h-40 overflow-y-auto bg-slate-50 border border-slate-100 rounded-lg p-3 text-[11.5px] text-slate-600 whitespace-pre-wrap">{att.attestation_text}</div>
                  {att.signed_at ? (
                    <p className="mt-2 text-xs font-semibold text-emerald-700">Signed by {att.signer_name} ({att.signer_title}) on {fmt(att.signed_at)}.</p>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2 flex-wrap">
                        <input value={f.name} onChange={e => setF({ name: e.target.value })} placeholder="Full name" className="flex-1 min-w-[10rem] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
                        <input value={f.title} onChange={e => setF({ title: e.target.value })} placeholder="Title" className="flex-1 min-w-[10rem] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
                      </div>
                      <label className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={f.consent} onChange={() => setF({ consent: !f.consent })} className="mt-0.5 accent-orange-600" />
                        I agree to sign electronically, and I certify the information submitted is accurate, truthful, and complete.
                      </label>
                      <button disabled={busy || !f.name.trim() || !f.title.trim() || !f.consent}
                        className="px-3.5 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-40"
                        onClick={async () => {
                          setBusy(true); setError('');
                          const r = await fetch(`${API}/api/fdr-portal/assessment/attestations/${att.id}/sign`, {
                            method: 'POST', headers: { 'Content-Type': 'application/json', 'x-portal-pass': pass },
                            body: JSON.stringify({ token, signer_name: f.name, signer_title: f.title, consent: f.consent }),
                          }).then(r => r.json()).catch(() => null);
                          if (r?.success) await refresh(); else setError(r?.message || 'Could not sign.');
                          setBusy(false);
                        }}>{busy ? 'Signing…' : 'Sign this attestation'}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 border-t border-slate-100 pt-4">
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)}
              placeholder="Anything the compliance team should know about your submission (optional)"
              className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
            <button disabled={busy} className={`${btn} mt-2`}
              onClick={async () => {
                setBusy(true); setError('');
                const r = await post('/assessment/submit', { note });
                if (r?.success) { await refresh(); setStep(5); } else setError(r?.message || 'Could not submit.');
                setBusy(false);
              }}>{busy ? 'Submitting…' : (a.submitted_at ? 'Submit updated response' : 'Submit to the compliance team')}</button>
            {(a.attestations || []).some(x => !x.signed_at) ? (
              <p className="mt-1.5 text-[11px] text-slate-400">You can submit your progress now and keep working — {(a.attestations || []).filter(x => !x.signed_at).length} attestation(s) still need a signature before the audit can conclude.</p>
            ) : (
              <p className="mt-1.5 text-[11px] text-slate-400">You can submit as often as you like while you complete your response — each submission goes to the compliance team.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Attach a file right on the element it proves — it lands in the same
 *  encrypted store, labeled with the requirement, and appears in the
 *  documents step too. */
// A question to the audit team, asked on the element it concerns. It is
// emailed to the auditor and saved on the audit record; the answer comes
// back by email (and the element may change — e.g. a document requirement
// relaxed to a written explanation).
function ItemQuestion({ token, pass, item }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [state, setState] = useState(''); // '' | busy | sent | error
  if (state === 'sent') return <span className="text-[11px] font-semibold text-emerald-700 self-center">Question sent — the audit team will reply by email</span>;
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 self-center">
        Ask the audit team
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1.5 flex-1 min-w-[16rem]">
      <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
        placeholder="e.g. May we answer this in writing instead of a document?"
        className="flex-1 border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-orange-400" />
      <button disabled={state === 'busy' || q.trim().length < 5}
        onClick={async () => {
          setState('busy');
          const r = await fetch(`${API}/api/fdr-portal/assessment/items/${item.id}/question?token=${encodeURIComponent(token)}`, {
            method: 'POST', headers: { 'x-portal-pass': pass, 'Content-Type': 'application/json' },
            body: JSON.stringify({ question: q.trim() }),
          }).then((x) => x.json()).catch(() => null);
          setState(r?.success ? 'sent' : 'error');
        }}
        className="px-2.5 py-1.5 rounded-lg bg-slate-900 text-white text-[11px] font-semibold disabled:opacity-40">
        {state === 'busy' ? 'Sending…' : state === 'error' ? 'Retry' : 'Send'}
      </button>
    </span>
  );
}

function ItemUpload({ token, pass, item, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <label className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold cursor-pointer ${busy ? 'opacity-50' : ''} border-slate-300 text-slate-600 hover:border-orange-400 hover:text-orange-700`}>
      {busy ? 'Uploading…' : err ? 'Retry upload' : 'Attach file'}
      <input type="file" multiple className="hidden" disabled={busy}
        onChange={async (e) => {
          // A requirement that asks for policies usually takes several files.
          // Every selected file is sent, so nothing is silently dropped.
          const picked = Array.from(e.target.files || []);
          e.target.value = '';
          if (!picked.length) return;
          const tooBig = picked.filter((f) => f.size > 25 * 1048576);
          const send = picked.filter((f) => f.size <= 25 * 1048576);
          setBusy(true); setErr('');
          let failed = 0;
          for (const f of send) {
          const fd = new FormData();
          fd.append('file', f);
          fd.append('item_id', item.id);
          fd.append('label', `${item.label_path ? `${item.label_path} — ` : ''}${item.criteria_text}`.slice(0, 300));
          const r = await fetch(`${API}/api/fdr-portal/documents?token=${encodeURIComponent(token)}`, {
            method: 'POST', headers: { 'x-portal-pass': pass }, body: fd,
          }).then(x => x.json()).catch(() => null);
          if (r?.success) onUploaded(f.name); else { failed += 1; setErr(r?.message || 'failed'); }
          }
          setBusy(false);
          if (!failed) {
            setErr(tooBig.length
              ? `${tooBig.length} file${tooBig.length === 1 ? '' : 's'} over 25 MB were not sent`
              : '');
          }
        }} />
    </label>
  );
}

/**
 * UniverseSlots — the vendor's universe and sample uploads, one labeled card
 * per request so there is never a question of which table goes where. Files
 * upload straight into the named slot; a received checkmark confirms it.
 */
function UniverseSlots({ a, token, pass, refresh, highlightPending = false }) {
  const [busySlot, setBusySlot] = useState(null);
  const [err, setErr] = useState('');

  const uploadTo = async (files, field, id) => {
    setErr('');
    for (const f of Array.from(files || [])) {
      if (f.size > 25 * 1048576) { setErr(`Files can be up to 25 MB; "${f.name}" is larger.`); continue; }
      setBusySlot(id);
      const fd = new FormData();
      fd.append('file', f);
      fd.append(field, id);
      const r = await fetch(`${API}/api/fdr-portal/documents?token=${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'x-portal-pass': pass }, body: fd,
      }).then(x => x.json()).catch(() => null);
      if (!r?.success) setErr(r?.message || `Could not upload "${f.name}".`);
    }
    setBusySlot(null);
    refresh();
  };

  const docsFor = (field, id) => (a.documents || []).filter(d => d[field] === id);

  const SlotCard = ({ id, field, title, sub, status, feedback, feedbackAt, children }) => {
    const files = docsFor(field, id);
    // The status is the truth: a returned universe reads "Needed" again even
    // though the earlier file is still listed below.
    const received = status === 'received' || (status !== 'requested' && files.length > 0);
    const returned = !received && !!feedback;
    return (
      <div className={`border rounded-xl p-4 ${!received && highlightPending ? 'border-orange-300 bg-orange-50/40' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-slate-800">{title}</div>
            {sub && <div className="mt-0.5 text-[11.5px] text-slate-500 whitespace-pre-wrap">{sub}</div>}
            {children}
          </div>
          <span className={`flex-none px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${received ? 'bg-emerald-50 text-emerald-700' : returned ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
            {received ? 'Received' : returned ? 'Needs correction' : 'Needed'}
          </span>
        </div>
        {returned && (
          <div className="mt-2 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-[11.5px] text-rose-800">
            <span className="font-bold">From the compliance team:</span> {feedback}
            <span className="block mt-0.5 text-[10px] text-rose-500">Fix the file and upload it again below.</span>
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map(d => (
              <div key={d.id} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">
                <span>📎</span><span className="truncate flex-1">{d.file_name}</span>
                <span className="text-emerald-600 font-semibold">Received</span>
              </div>
            ))}
          </div>
        )}
        <label className="mt-2.5 inline-block px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold cursor-pointer hover:bg-slate-800">
          {busySlot === id ? 'Uploading…' : `Upload ${files.length ? 'another file' : 'here'}`}
          <input type="file" multiple className="hidden" disabled={busySlot === id}
            onChange={e => { uploadTo(e.target.files, field, id); e.target.value = ''; }} />
        </label>
      </div>
    );
  };

  return (
    <div className={`${card} p-6`}>
      <h3 className="text-sm font-bold text-slate-800">Universes &amp; requested case files</h3>
      <p className="text-xs text-slate-500 mt-1">
        Each request below is its own upload — put each file in its named slot, exactly as labeled. Files are encrypted on arrival and travel only through this portal, never by email.
      </p>
      {err && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-2">{err}</p>}
      <div className="mt-3 space-y-3">
        {(a.universe_requests || []).map(u => (
          <SlotCard key={u.id} id={u.id} field="universe_request_id" title={u.label} sub={u.instructions}
            status={u.status} feedback={u.feedback} feedbackAt={u.feedback_at} />
        ))}
        {(a.sample_requests || []).map(s => (
          <SlotCard key={s.id} id={s.id} field="sample_request_id"
            title={`Case files requested · ${s.universe_label}`}
            sub={s.note} status={s.status}>
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] font-semibold text-slate-500">The requested case identifiers ({s.identifiers.split('\n').filter(Boolean).length})</summary>
              <pre className="mt-1 text-[11px] text-slate-600 bg-slate-50 rounded-lg p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">{s.identifiers}</pre>
            </details>
          </SlotCard>
        ))}
      </div>
    </div>
  );
}

/**
 * ReportCard — the audit report round, vendor side. The draft renders in
 * full; each finding shows the verdict and the auditor's observation with a
 * rebuttal box and a file attach; one submit sends every rebuttal. The
 * final renders read-only with each disposition. Everything stays inside
 * the secured session — the report never travels as an attachment.
 */
function ReportCard({ a, token, pass, post, refresh }) {
  const r = a.report;
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }) : '');
  const submitted = (r.flagged || []).some(f => f.rebuttal_at);

  return (
    <div className={`${card} p-6`}>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-800">{r.is_final ? 'Final audit report' : 'Draft audit report'}</h3>
          <p className="text-xs text-slate-500 mt-1">
            {r.is_final
              ? `Issued ${fmt(r.final_at)}. Each management response and its disposition is part of the record.`
              : `For each finding, state your position — agree, partially agree, or disagree — with your written response${r.rebuttal_deadline ? `, no later than ${fmt(r.rebuttal_deadline)}` : ''}. Responses print verbatim in the final report.`}
          </p>
        </div>
        <span className="flex-none flex items-center gap-2">
          <button onClick={async () => {
            const { exportAsmtReportPdf, fetchLogoDataUrl } = await import('./FDR/exportAsmtReportPdf');
            const logoDataUrl = await fetchLogoDataUrl(`${API}/api/fdr-portal/logo?token=${encodeURIComponent(token)}`);
            exportAsmtReportPdf({ text: r.text, sha256: r.sha256 || null, logoDataUrl, fileBase: `${r.is_final ? 'FINAL' : 'DRAFT'} Audit Report - ${a.cycle_label}` });
          }} className="px-3 py-1.5 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-[11.5px] font-semibold">
            Download the report (PDF)
          </button>
          <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${r.is_final ? 'bg-slate-900 text-white' : 'bg-amber-50 text-amber-700'}`}>
            {r.is_final ? 'FINAL' : 'DRAFT'}
          </span>
        </span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">Download it, circulate it to whoever prepares your rebuttals, and keep it for your records — then submit the rebuttals below.</p>

      <div className="mt-3 max-h-96 overflow-y-auto border border-slate-200 rounded-xl">
        <VendorReportDoc text={r.text} />
      </div>
      {r.is_final && r.sha256 && (
        <div className="mt-1.5 text-[10px] text-slate-400 font-mono">Report fingerprint (SHA-256): {r.sha256}</div>
      )}

      {!r.is_final && (r.flagged || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Management response — one per finding</div>
          <div className="space-y-3">
            {r.flagged.map((f) => {
              const d = drafts[f.id] || { position: null, rebuttal: '', action: '', owner: '', target_date: '' };
              const setD = (patch) => setDrafts({ ...drafts, [f.id]: { ...d, ...patch } });
              const agreeing = d.position === 'agree' || d.position === 'partially_agree';
              return (
                <div key={f.id} className="border border-slate-200 rounded-xl p-3.5">
                  {f.label_path && <span className="inline-block mb-1 px-1.5 py-px rounded bg-slate-100 text-[10px] font-semibold text-slate-500">{f.label_path}</span>}
                  <div className="text-[12.5px] text-slate-800 leading-snug">{f.criteria_text}</div>
                  <div className="mt-1 text-[11.5px]">
                    <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${f.verdict === 'not_met' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                      {f.verdict === 'not_met' ? 'Not Met' : 'Partially Met'}
                    </span>
                    {f.auditor_comments && <span className="ml-2 text-slate-500">{f.auditor_comments}</span>}
                  </div>
                  {/* The exchange so far — every turn, yours and the auditor's. */}
                  {(f.exchanges || []).map((x, xi) => (
                    <div key={xi} className={`mt-2 border-l-2 rounded-r-lg px-2.5 py-1.5 text-[11.5px] text-slate-700 ${x.author === 'vendor' ? 'bg-sky-50/70 border-sky-300' : x.position === 'accepted' ? 'bg-emerald-50/70 border-emerald-300' : 'bg-rose-50/60 border-rose-300'}`}>
                      <span className={`text-[9.5px] font-bold uppercase tracking-wider mr-1.5 ${x.author === 'vendor' ? 'text-sky-600' : x.position === 'accepted' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {x.author === 'vendor'
                          ? (x.position === 'agree' ? 'You agree' : x.position === 'partially_agree' ? 'You partially agree' : 'You disagree')
                          : (x.position === 'accepted' ? 'Auditor — accepted' : 'Auditor — not accepted')}
                      </span>
                      {x.body}
                      {x.mgmt_action && <div className="mt-1 text-[11px] text-slate-600">Committed action: {x.mgmt_action}{x.mgmt_owner ? ` — ${x.mgmt_owner}` : ''}{x.mgmt_target_date ? ` — by ${String(x.mgmt_target_date).slice(0, 10)}` : ''}</div>}
                    </div>
                  ))}
                  {/* Rebut again until the final issues: after an auditor
                      disposition, the response box returns for your counter. */}
                  {f.rebuttal_at && !f.rebuttal_disposition ? (
                    <p className="mt-1.5 text-[11px] text-slate-400">Your response is with the auditor. You may respond again once the auditor rules on it.</p>
                  ) : (
                    <div className="mt-2.5 space-y-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Your position:</span>
                        {[['agree', 'Agree'], ['partially_agree', 'Partially agree'], ['disagree', 'Disagree']].map(([k, label]) => (
                          <button key={k} onClick={() => setD({ position: k })}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ring-1 ${d.position === k
                              ? (k === 'disagree' ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200')
                              : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 flex-wrap items-start">
                        <textarea rows={2} value={d.rebuttal} onChange={e => setD({ rebuttal: e.target.value })}
                          placeholder={f.rebuttal_disposition ? 'Your counter-response to the auditor’s disposition — prints verbatim in the final report' : d.position === 'disagree' ? 'Why you disagree — your response prints verbatim in the final report' : 'Your written response'}
                          className="flex-1 min-w-[16rem] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-orange-400" />
                        <ItemUpload token={token} pass={pass} item={f} onUploaded={() => refresh()} />
                      </div>
                      {agreeing && (
                        <div className="flex gap-2 flex-wrap">
                          <input value={d.action} onChange={e => setD({ action: e.target.value })}
                            placeholder="Committed corrective action"
                            className="flex-1 min-w-[14rem] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
                          <input value={d.owner} onChange={e => setD({ owner: e.target.value })}
                            placeholder="Responsible owner"
                            className="w-44 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
                          <input type="date" value={d.target_date} onChange={e => setD({ target_date: e.target.value })}
                            title="Target completion date"
                            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {err && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-2">{err}</p>}
          <button disabled={busy || !Object.values(drafts).some(v => v && v.position && v.rebuttal && v.rebuttal.trim())} className={`${btn} mt-3`}
            onClick={async () => {
              setBusy(true); setErr('');
              const rebuttals = Object.entries(drafts)
                .filter(([, v]) => v && v.position && v.rebuttal && v.rebuttal.trim())
                .map(([item_id, v]) => ({ item_id, position: v.position, rebuttal: v.rebuttal, action: v.action, owner: v.owner, target_date: v.target_date || null }));
              const res = await post('/assessment/rebuttals', { rebuttals });
              if (res?.success) { setDrafts({}); await refresh(); } else setErr(res?.message || 'Could not submit.');
              setBusy(false);
            }}>{busy ? 'Submitting…' : 'Submit management responses'}</button>
          {submitted && <p className="mt-2 text-xs text-slate-500">Every turn is on the record. You may respond again after each auditor disposition, until the final report issues.</p>}
        </div>
      )}

      {r.is_final && (r.flagged || []).some(f => f.vendor_rebuttal) && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Dispositions</div>
          {r.flagged.filter(f => f.vendor_rebuttal).map((f) => (
            <div key={f.id} className="py-1.5 border-t border-slate-100 first:border-t-0 text-[11.5px]">
              <div className="text-slate-700">{f.label_path || f.criteria_text.slice(0, 80)}</div>
              <div className={`mt-0.5 font-semibold ${f.rebuttal_disposition === 'accepted' ? 'text-emerald-700' : 'text-rose-700'}`}>
                {f.rebuttal_disposition === 'accepted' ? 'Accepted — the finding was amended' : 'Not accepted — the finding stands'}
                {f.rebuttal_note ? <span className="text-slate-500 font-normal"> · {f.rebuttal_note}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** The report typeset like the PDF reads — a document, never a code box. */
function VendorReportDoc({ text }) {
  const lines = String(text || '').split('\n');
  return (
    <div className="bg-white px-6 py-5">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        if (i === 0) return <div key={i} className="font-serif text-[18px] font-bold text-slate-900 border-b-2 border-slate-800 pb-2 mb-2.5 leading-snug">{line}</div>;
        if (/^[IVX]+\.\s/.test(line)) return <div key={i} className="mt-3.5 mb-1 text-[11.5px] font-bold text-slate-900 uppercase tracking-wide border-b border-slate-200 pb-1">{line}</div>;
        if (/^(Finding \d+|Rebuttal \d+)/.test(line)) return <div key={i} className={`mt-2 text-[12.5px] font-bold ${/Not Met/.test(line) ? 'text-rose-700' : 'text-slate-900'}`}>{line}</div>;
        if (/^(Delegated Entity|Audit Cycle|Audit Period|Issued by|Date of issuance|To|Cc):/.test(line)) return <div key={i} className="text-[11.5px] text-slate-500">{line}</div>;
        return <p key={i} className="font-serif text-[12.5px] text-slate-800 leading-relaxed my-0.5">{line}</p>;
      })}
    </div>
  );
}
